import { useState, Fragment } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Instagram, ChevronDown } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Logo } from "./Logo";
import { Avatar } from "./Avatar";
import { INSTAGRAM_URL } from "../lib/social";
import { ART_CATEGORIES } from "../lib/categories";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
  SheetTitle,
  SheetDescription,
} from "./ui/sheet";

const NAV_LINKS = [
  { to: "/gallery", label: "Gallery", testid: "nav-gallery" },
  { to: "/auctions", label: "Auctions", testid: "nav-auctions" },
  { to: "/artists", label: "Artists", testid: "nav-artists" },
  { to: "/art-hubs", label: "Art Hubs", testid: "nav-art-centers" },
  { to: "/events", label: "Events", testid: "nav-events" },
  { to: "/journal", label: "African Stories", testid: "nav-journal" },
];

export const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);

  const handleLogout = () => {
    logout();
    setOpen(false);
    navigate("/");
  };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 w-full bg-white/80 backdrop-blur-md border-b border-black/5"
    >
      <div className="px-6 md:px-10 lg:px-12 xl:px-24 h-20 flex items-center justify-between gap-4">
        <Link to="/" data-testid="home-link" className="flex items-center flex-shrink-0">
          <Logo size={36} />
        </Link>

        {/* Desktop nav (xl+) — below xl the hamburger sheet takes over so the
            6-item menu never overflows phones, tablets or small laptops */}
        <nav className="hidden xl:flex items-center gap-10 label-eyebrow">
          {NAV_LINKS.map((l) => (
            <Fragment key={l.to}>
              <Link
                data-testid={l.testid}
                to={l.to}
                className="whitespace-nowrap hover:text-[#1E3A8A] transition-colors"
              >
                {l.label}
              </Link>
              {l.to === "/gallery" && (
                <div className="relative group">
                  <button
                    data-testid="nav-categories"
                    className="flex items-center gap-1 whitespace-nowrap hover:text-[#1E3A8A] transition-colors"
                  >
                    Categories
                    <ChevronDown className="w-3.5 h-3.5 transition-transform group-hover:rotate-180" strokeWidth={2} />
                  </button>
                  {/* Hover dropdown — pt-3 keeps the panel reachable across the gap */}
                  <div className="absolute left-0 top-full pt-3 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200 z-50">
                    <div
                      data-testid="nav-categories-panel"
                      className="min-w-[210px] bg-white border border-[#E5E0D8] shadow-[0_12px_30px_-8px_rgba(42,24,15,0.25)] py-2"
                    >
                      {ART_CATEGORIES.map((c) => (
                        <Link
                          key={c.slug}
                          to={`/category/${c.slug}`}
                          data-testid={`nav-category-${c.slug}`}
                          className="block px-5 py-2.5 text-[#111] hover:bg-[#F4EFE6] hover:text-[#1E3A8A] transition-colors"
                        >
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </Fragment>
          ))}
        </nav>

        {/* Desktop auth actions (xl+) */}
        <div className="hidden xl:flex items-center gap-4 label-eyebrow">
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="AfricaArtMasters on Instagram"
            data-testid="header-instagram-link"
            className="text-[#111] hover:text-[#1E3A8A] transition-colors"
          >
            <Instagram className="w-5 h-5" strokeWidth={1.5} />
          </a>
          {user ? (
            <>
              {user.role === "admin" && (
                <Link data-testid="nav-admin" to="/admin" className="hover:text-[#1E3A8A] transition-colors">
                  Admin
                </Link>
              )}
              <Link
                data-testid="nav-dashboard"
                to="/dashboard"
                className="flex items-center gap-2 hover:text-[#1E3A8A] transition-colors"
              >
                <Avatar
                  src={user.avatar_url}
                  name={user.name}
                  size="sm"
                  testid="header-avatar"
                />
                <span>{user.role === "artist" ? "Studio" : "Account"}</span>
              </Link>
              <button
                data-testid="logout-btn"
                onClick={handleLogout}
                className="hover:text-[#1E3A8A] transition-colors"
              >
                Logout
              </button>
            </>
          ) : (
            <>
              <Link data-testid="nav-login" to="/login" className="hover:text-[#1E3A8A] transition-colors">
                Login
              </Link>
              <Button
                data-testid="nav-join"
                onClick={() => navigate("/register")}
                className="rounded-none bg-[#1E3A8A] hover:bg-[#162a66] text-white px-5 py-2 h-auto label-eyebrow"
              >
                Join
              </Button>
            </>
          )}
        </div>

        {/* Mobile/tablet trigger (< xl) */}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button
              data-testid="mobile-menu-trigger"
              aria-label="Open menu"
              className="xl:hidden inline-flex items-center justify-center w-11 h-11 -mr-2 text-[#111] hover:text-[#1E3A8A] transition-colors"
            >
              <Menu className="w-6 h-6" strokeWidth={1.5} />
            </button>
          </SheetTrigger>
          <SheetContent
            side="right"
            data-testid="mobile-menu-panel"
            className="w-[88%] sm:max-w-md p-0 rounded-none border-l border-[#E5E0D8] bg-white flex flex-col overflow-y-auto"
          >
            <SheetTitle className="sr-only">AfricaArtMasters menu</SheetTitle>
            <SheetDescription className="sr-only">
              Site navigation and account actions
            </SheetDescription>

            <div className="flex items-center justify-between px-6 h-16 sm:h-20 flex-shrink-0 border-b border-[#E5E0D8]">
              <Link
                to="/"
                onClick={() => setOpen(false)}
                className="flex items-center"
                data-testid="mobile-home-link"
              >
                <Logo size={36} />
              </Link>
              <SheetClose asChild>
                <button
                  aria-label="Close menu"
                  data-testid="mobile-menu-close"
                  className="inline-flex items-center justify-center w-11 h-11 -mr-2 text-[#111] hover:text-[#1E3A8A]"
                >
                  <X className="w-6 h-6" strokeWidth={1.5} />
                </button>
              </SheetClose>
            </div>

            <nav className="flex flex-col px-6 py-4 sm:py-6">
              {NAV_LINKS.map((l) => (
                <Fragment key={l.to}>
                  <Link
                    to={l.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-${l.testid}`}
                    className="font-serif-display text-2xl sm:text-3xl tracking-tight font-light py-3 sm:py-4 border-b border-[#E5E0D8] text-[#111] hover:text-[#1E3A8A] transition-colors"
                  >
                    {l.label}
                  </Link>
                  {l.to === "/gallery" && (
                    <div className="border-b border-[#E5E0D8]">
                      <button
                        type="button"
                        onClick={() => setCatOpen((v) => !v)}
                        data-testid="mobile-nav-categories"
                        aria-expanded={catOpen}
                        className="w-full flex items-center justify-between font-serif-display text-2xl sm:text-3xl tracking-tight font-light py-3 sm:py-4 text-[#111] hover:text-[#1E3A8A] transition-colors"
                      >
                        Categories
                        <ChevronDown
                          className={`w-6 h-6 transition-transform ${catOpen ? "rotate-180" : ""}`}
                          strokeWidth={1.5}
                        />
                      </button>
                      {catOpen && (
                        <div data-testid="mobile-categories-panel" className="pb-3 pl-4">
                          {ART_CATEGORIES.map((c) => (
                            <Link
                              key={c.slug}
                              to={`/category/${c.slug}`}
                              onClick={() => setOpen(false)}
                              data-testid={`mobile-nav-category-${c.slug}`}
                              className="block label-eyebrow text-[#555] hover:text-[#1E3A8A] transition-colors py-2.5"
                            >
                              {c.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </Fragment>
              ))}
            </nav>

            <div className="px-6 pb-8 mt-2 sm:mt-4 label-eyebrow">
              {user ? (
                <div className="flex flex-col gap-3">
                  <p className="text-xs text-[#555] font-mono-aam">
                    {user.role.toUpperCase()} · {user.email}
                  </p>
                  {user.role === "admin" && (
                    <Link
                      to="/admin"
                      onClick={() => setOpen(false)}
                      data-testid="mobile-nav-admin"
                      className="border border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white transition-colors px-5 py-4 text-center"
                    >
                      Admin Console
                    </Link>
                  )}
                  <Link
                    to="/dashboard"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-nav-dashboard"
                    className="border border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white transition-colors px-5 py-4 text-center"
                  >
                    {user.role === "artist" ? "Studio" : "Account"}
                  </Link>
                  <button
                    onClick={handleLogout}
                    data-testid="mobile-logout-btn"
                    className="border border-[#E5E0D8] text-[#555] hover:text-[#D32F2F] hover:border-[#D32F2F] transition-colors px-5 py-4"
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  <Link
                    to="/login"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-nav-login"
                    className="border border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white transition-colors px-5 py-4 text-center"
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-nav-join"
                    className="bg-[#1E3A8A] hover:bg-[#162a66] text-white transition-colors px-5 py-4 text-center"
                  >
                    Join AfricaArtMasters
                  </Link>
                </div>
              )}
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-instagram-link"
                className="mt-6 inline-flex items-center gap-2 text-sm text-[#555] hover:text-[#1E3A8A] transition-colors"
              >
                <Instagram className="w-4 h-4" strokeWidth={1.5} />
                Follow on Instagram
              </a>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};
